import { API_BASE as BASE_URL, getAuthHeaders, apiFetch } from './api-fetch';
import { API, buildPath } from './api-endpoints';
import { Appointment } from '../types/appointment';

/** Map backend status (lowercase) → frontend status (Title Case) */
function mapStatus(s: string): Appointment['status'] {
  const map: Record<string, Appointment['status']> = {
    pending: 'Scheduled',
    scheduled: 'Scheduled',
    confirmed: 'Confirmed',
    completed: 'Completed',
    cancelled: 'Cancelled',
    'no show': 'No Show',
    noshow: 'No Show',
  };
  return map[(s || '').toLowerCase()] ?? 'Scheduled';
}

/** Map backend type (lowercase) → frontend type (Title Case) */
function mapType(t: string): Appointment['type'] {
  const map: Record<string, Appointment['type']> = {
    viewing: 'Property Viewing',
    'property viewing': 'Property Viewing',
    signing: 'Contract Signing',
    'contract signing': 'Contract Signing',
    consultation: 'Initial Consultation',
    'initial consultation': 'Initial Consultation',
    walkthrough: 'Final Walkthrough',
    'final walkthrough': 'Final Walkthrough',
    transfer: 'Transfer Documents',
    'transfer documents': 'Transfer Documents',
  };
  return map[(t || '').toLowerCase()] ?? 'Initial Consultation';
}

/** Map backend location (lowercase) → frontend location (Title Case) */
function mapLocation(l: string): Appointment['location'] {
  const map: Record<string, Appointment['location']> = {
    office: 'Office',
    property: 'Property',
    'client location': 'Client Location',
    virtual: 'Virtual',
  };
  return map[(l || '').toLowerCase()] ?? 'Office';
}

/** Normalize a raw backend appointment item to the frontend Appointment shape */
function normalizeAppointment(item: any): Appointment {
  // scheduledDate may be ISO string "2026-04-10T09:00:00Z" or "2026-04-10 09:00"
  let date = item.date ?? '';
  let time = item.time ?? '';
  if (!date && item.scheduledDate) {
    const d = new Date(item.scheduledDate);
    if (!isNaN(d.getTime())) {
      date = d.toISOString().split('T')[0];
      time = d.toTimeString().slice(0, 5);
    } else {
      // Try splitting string directly
      const parts = item.scheduledDate.split(/T| /);
      date = parts[0] ?? '';
      time = (parts[1] ?? '').slice(0, 5);
    }
  }

  return {
    id: item.id ?? item.appointmentId ?? item.PK?.replace('APPOINTMENT#', '') ?? '',
    clientId: item.clientId ?? item.clientEmail ?? '',
    clientName: item.clientName ?? '',
    clientEmail: item.clientEmail ?? '',
    clientPhone: item.clientPhone ?? '',
    propertyId: item.propertyId ?? item.listingId ?? undefined,
    propertyAddress: item.propertyAddress ?? '',
    type: mapType(item.type),
    date,
    time,
    duration: item.duration ?? 60,
    status: mapStatus(item.status),
    location: mapLocation(item.location),
    locationDetails: item.locationDetails ?? '',
    notes: item.notes ?? '',
    reminders: item.reminders ?? { email: true, sms: false, whatsapp: false },
    documents: item.documents ?? [],
    createdAt: typeof item.createdAt === 'number'
      ? new Date(item.createdAt).toISOString()
      : item.createdAt ?? new Date().toISOString(),
    updatedAt: typeof item.updatedAt === 'number'
      ? new Date(item.updatedAt).toISOString()
      : item.updatedAt ?? new Date().toISOString(),
    agentId: item.agentId ?? '',
    agentName: item.agentName ?? '',
  };
}

// Get all appointments
export async function getAppointments(): Promise<Appointment[]> {
  try {
    const response = await apiFetch(`${BASE_URL}${API.appointments.list.path}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch appointments');
    }

    const data = await response.json();
    const raw = data.data?.appointments ?? data.appointments ?? [];
    return raw.map(normalizeAppointment);
  } catch (error) {
    console.error('Error fetching appointments:', error);
    throw error;
  }
}

// Get single appointment
export async function getAppointment(id: string): Promise<Appointment> {
  try {
    const response = await apiFetch(`${BASE_URL}${buildPath(API.appointments.getById.path, { id })}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || 'Failed to fetch appointment');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error fetching appointment:', error);
    throw error;
  }
}

// Create appointment
export async function createAppointment(appointmentData: Partial<Appointment>): Promise<Appointment> {
  try {
    const response = await apiFetch(`${BASE_URL}${API.appointments.create.path}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(appointmentData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create appointment');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error creating appointment:', error);
    throw error;
  }
}

// Update appointment
export async function updateAppointment(id: string, appointmentData: Partial<Appointment>): Promise<Appointment> {
  try {
    const response = await apiFetch(`${BASE_URL}${buildPath(API.appointments.update.path, { id })}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(appointmentData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update appointment');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error updating appointment:', error);
    throw error;
  }
}

// Delete appointment
export async function deleteAppointment(id: string): Promise<void> {
  try {
    const response = await apiFetch(`${BASE_URL}${buildPath(API.appointments.delete.path, { id })}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete appointment');
    }
  } catch (error) {
    console.error('Error deleting appointment:', error);
    throw error;
  }
}

// Cancel appointment — PUT /appointments/{id}/cancel
export async function cancelAppointment(id: string): Promise<Appointment> {
  try {
    const response = await apiFetch(`${BASE_URL}${buildPath(API.appointments.cancel.path, { id })}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || 'Failed to cancel appointment');
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.error('Error cancelling appointment:', error);
    throw error;
  }
}

// Seed appointments
export async function seedAppointments(appointments: Appointment[]): Promise<void> {
  try {
    const response = await apiFetch(`${BASE_URL}/seed-appointments`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ appointments }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to seed appointments');
    }

    const data = await response.json();
    console.log(`✅ Seeded ${data.count} appointments`);
  } catch (error) {
    console.error('Error seeding appointments:', error);
    throw error;
  }
}

export const appointmentAPI = {
  getAppointments,
  getAppointment,
  createAppointment,
  updateAppointment,
  deleteAppointment,
  cancelAppointment,
  seedAppointments,
};